import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LookupSelect } from "@/components/app/LookupSelect";
import { RegSitePicker, defaultIds } from "@/components/order/RegSitePicker";
import { ProspectConvert } from "@/components/sales/ProspectConvert";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

// Quote -> draft LM. A prospect must first become (or be linked to) a customer.
export function ConvertToLmDialog({ q, onClose, onDone }) {
  const navigate = useNavigate();
  const cid = q.party_type === "customer" ? q.customer_id : q.prospect?.converted_customer_id;
  const { data: customer } = useFetch(cid ? `/customers/${cid}` : null);
  const { data: types } = useFetch("/lm-types?active=true");
  const [f, setF] = useState({ prefix: "", customer_po_no: "", po_date: "", confirmation_type: "PO" });
  const [ids, setIds] = useState(null);
  const cur = ids || (customer ? { ...defaultIds(customer), regId: q.registration_id || defaultIds(customer).regId } : { regId: null, siteId: null });
  const go = async () => {
    try {
      const r = await api.post(`/quotes/${q.id}/convert-to-lm`, { ...f, customer_id: cid, registration_id: cur.regId, site_id: cur.siteId });
      toast.success(`${r.data.order_no} created from ${q.ref}`); onDone(); navigate(`/orders/${r.data.order_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="quote-convert-dialog">
        <DialogHeader><DialogTitle>Convert {q.ref} to an LM</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Creates a draft LM with this revision's lines at the quoted net prices (after discount), its terms and a link back to {q.ref}. Manual lines go across as free-text lines.</p>
        {!cid ? <ProspectConvert prospect={q.prospect} onDone={onDone} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 text-sm">Customer: <b data-testid="quote-convert-customer">{customer?.name || "…"}</b></div>
            {customer && <RegSitePicker customer={customer} ids={cur} onChange={setIds} />}
            <div><Label>LM type *</Label>
              <Select value={f.prefix} onValueChange={(v) => setF({ ...f, prefix: v })}>
                <SelectTrigger className="bg-white" data-testid="quote-convert-prefix"><SelectValue placeholder="Pick LM type" /></SelectTrigger>
                <SelectContent>{(types || []).filter((t) => !t.projection_by_default).map((t) => <SelectItem key={t.prefix} value={t.prefix}>{t.prefix} — {t.description}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Confirmation type</Label><LookupSelect type="confirmation_type" value={f.confirmation_type} onChange={(v) => setF({ ...f, confirmation_type: v })} testid="quote-convert-confirmation" allowEmpty={false} /></div>
            <div><Label>Customer PO number</Label><Input value={f.customer_po_no} onChange={(e) => setF({ ...f, customer_po_no: e.target.value })} data-testid="quote-convert-po" /></div>
            <div><Label>PO date</Label><Input type="date" value={f.po_date} onChange={(e) => setF({ ...f, po_date: e.target.value })} data-testid="quote-convert-po-date" /></div>
          </div>
        )}
        <DialogFooter><Button disabled={!cid || !f.prefix} onClick={go} data-testid="quote-convert-submit">Create LM</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
